import {ChangeDetectionStrategy, Component, ElementRef, inject, viewChild} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {AiChatFacade} from '../../ai-chat.facade';

/**
 * Chat message input textarea with streaming stop button.
 *
 * Reads draft, streaming state, and canSend directly from injected facade.
 * Calls facade.send() on button click or Ctrl+Enter.
 * Supports Shift+Enter for multi-line input (not sent).
 */
@Component({
  selector: 'app-chat-input',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule],
  templateUrl: './chat-input.component.html',
  styleUrl: './chat-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatInputComponent {
  protected readonly facade = inject(AiChatFacade);
  private readonly textareaRef = viewChild<ElementRef<HTMLTextAreaElement>>('textareaEl');

  constructor() {
    this.facade.insertAsset$.pipe(takeUntilDestroyed()).subscribe((text) => this.insertAtCursor(text));
  }

  private insertAtCursor(text: string): void {
    const el = this.textareaRef()?.nativeElement;
    const draft = this.facade.draft();
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? start;
    this.facade.draft.set(draft.slice(0, start) + text + draft.slice(end));
    if (el) {
      const cursor = start + text.length;
      setTimeout(() => { el.setSelectionRange(cursor, cursor); el.focus(); });
    }
  }

  onEnter(e: any): void {
    if (e.shiftKey) return;
    e.preventDefault();
    this.facade.send();
  }
}
